/* eslint-disable react-refresh/only-export-components */
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'

import { useAppStyles } from '../styles/common.styles'

type DialogOptions = {
  title?: string
  message?: string | ReactNode
  confirmText?: string
  cancelText?: string
  showX?: boolean
  hideCancel?: boolean
  hideConfirm?: boolean
}

type DialogState = DialogOptions & { resolve?: (value: boolean) => void }

interface DialogContextProps {
  open: (options: DialogOptions) => Promise<boolean>
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined)

export function DialogProvider({ children }: PropsWithChildren) {
  const styles = useAppStyles()
  const [state, setState] = useState<DialogState | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback((options: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...options, resolve })
      setIsOpen(true)
    })
  }, [])

  const handleClose = (result: boolean) => {
    state?.resolve?.(result)
    setIsOpen(false)
  }

  const value = useMemo(() => ({ open }), [open])

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Dialog open={isOpen} onOpenChange={(_e, data) => setIsOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            {state?.title || state?.showX ? (
              <DialogTitle
                action={
                  state.showX ? (
                    <DialogTrigger action="close">
                      <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                    </DialogTrigger>
                  ) : null
                }
              >
                {state.title}
              </DialogTitle>
            ) : null}
            {state?.message && (
              <DialogContent className={styles.preLine}>{state.message}</DialogContent>
            )}
            <DialogActions>
              {!state?.hideCancel && (
                <Button onClick={() => handleClose(false)}>{state?.cancelText ?? 'Cancel'}</Button>
              )}
              {!state?.hideConfirm && (
                <Button appearance="primary" onClick={() => handleClose(true)}>
                  {state?.confirmText ?? 'Confirm'}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </DialogContext.Provider>
  )
}

export function useDialog() {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }
  return context
}
